import { useEffect, useState } from 'react'
import { createTodo } from '@core/letters'
import { loadFamily, type FamilyMember, type FamilySession } from '@core/session'
import { Button, ErrorNote, Field } from '@design/components'
import { Overlay } from '@design/Overlay'

/**
 * Adding something that needs doing.
 *
 * One required field. Everything else is optional and stays out of the way
 * until you want it — a to-do list you have to fill in a form for is a to-do
 * list nobody uses.
 *
 * The one place it insists: handing a task to someone requires saying what
 * they should actually do. "Verzekering" means something to you and nothing to
 * your mother; "Bel de verzekering, vraag naar de nieuwe polis" is a task she
 * can finish. The database will take an empty note — this is a product rule,
 * so it lives where someone can be told why.
 */
export function TodoForm({
  session,
  onDone,
  onCancel,
}: {
  session: FamilySession
  onDone: () => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [spaceId, setSpaceId] = useState(session.spaces[0]?.id ?? '')
  const [assignedTo, setAssignedTo] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [family, setFamily] = useState<FamilyMember[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadFamily(session.accountId)
      .then((all) => setFamily(all.filter((m) => m.userId !== session.userId)))
      .catch(() => setFamily([]))
  }, [session.accountId, session.userId])

  async function save() {
    if (!title.trim()) {
      setError('Waar gaat het over?')
      return
    }
    setBusy(true)
    setError('')
    try {
      await createTodo({
        spaceId,
        title,
        detail: null,
        dueDate: dueDate || null,
        assignedTo,
        assignmentNote: assignedTo ? note : null,
      })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan lukte niet.')
      setBusy(false)
    }
  }

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <button onClick={onCancel} className="text-muted px-2 py-2">
          Annuleren
        </button>
        <span className="font-medium">Nieuwe taak</span>
        <span className="w-20" />
      </header>

      <div className="flex-1 scroll-area px-4 py-4">
        <Field
          label="Wat moet er gebeuren?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Bel de verzekering"
          autoFocus
        />

        <Field
          label="Wanneer? (mag leeg blijven)"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />

        {session.spaces.length > 1 && (
          <>
            <p className="text-sm text-muted mb-1.5">Waarvoor?</p>
            <div className="flex gap-2 mb-4">
              {session.spaces.map((space) => (
                <button
                  key={space.id}
                  onClick={() => setSpaceId(space.id)}
                  className={`flex-1 min-h-[3rem] rounded-xl border text-sm font-medium ${
                    space.id === spaceId ? 'border-ink bg-ink/5' : 'border-line text-muted'
                  }`}
                >
                  {space.name}
                </button>
              ))}
            </div>
          </>
        )}

        {family.length > 0 && (
          <>
            <p className="text-sm text-muted mb-1.5">Wie doet het?</p>
            <div className="flex flex-wrap gap-2 mb-4">
              <Choice label="Ik" on={assignedTo === null} onPick={() => setAssignedTo(null)} />
              {family.map((member) => (
                <Choice
                  key={member.userId}
                  label={member.displayName}
                  on={assignedTo === member.userId}
                  onPick={() => setAssignedTo(member.userId)}
                />
              ))}
            </div>
          </>
        )}

        {assignedTo && (
          <Field
            label="Wat moeten ze precies doen?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Vraag naar de nieuwe polis"
            hint="Ze krijgen dit als bericht, dus schrijf het zoals je het zou zeggen."
          />
        )}

        <ErrorNote>{error}</ErrorNote>
      </div>

      <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom border-t border-line">
        <Button size="large" onClick={save} disabled={busy}>
          {busy ? 'Bezig…' : 'Toevoegen'}
        </Button>
      </div>
    </Overlay>
  )
}

function Choice({ label, on, onPick }: { label: string; on: boolean; onPick: () => void }) {
  return (
    <button
      onClick={onPick}
      className={`min-h-[2.75rem] px-4 rounded-xl border text-sm font-medium ${
        on ? 'border-ink bg-ink/5' : 'border-line text-muted'
      }`}
    >
      {label}
    </button>
  )
}
